import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelySuzanMode({ brand }: ModeRendererProps) {
  // Optical intelligence; the geometry of trust
  // Trust grid, op-dots, secure lattice - the signature Suzan AI aesthetic
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.deep} />
      
      {/* Trust grid - secure lattice foundation */}
      <defs>
        <pattern id="trust-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <rect x="0" y="0" width="40" height="40" fill="none" stroke={brand.aqua} strokeWidth="0.5" opacity={0.2} />
          <circle cx="20" cy="20" r="2" fill={brand.aqua} opacity={0.4} />
        </pattern>
        
        <radialGradient id="suzan-glow" cx="50%" cy="50%">
          <stop offset="0%" stopColor={brand.aqua} stopOpacity="0.8" />
          <stop offset="50%" stopColor={brand.violet} stopOpacity="0.5" />
          <stop offset="100%" stopColor={brand.deep} stopOpacity="0" />
        </radialGradient>
      </defs>
      
      {/* Background trust grid */}
      <rect x="0" y="0" width="794" height="1123" fill="url(#trust-grid)" />
      
      {/* Central optical trust symbol - Suzan AI logo-inspired */}
      <g transform="translate(397, 280)">
        {/* Concentric trust rings with Vasarely distortion */}
        {Array.from({ length: 12 }).map((_, i) => {
          const r = 30 + i * 16;
          const distortion = Math.sin(i * 0.5) * 4;
          const segments = 24;
          
          return (
            <g key={`trust-ring-${i}`}>
              {Array.from({ length: segments }).map((_, j) => {
                const angle1 = (j / segments) * Math.PI * 2;
                const angle2 = ((j + 1) / segments) * Math.PI * 2;
                const modulation = Math.sin(j * 0.8 + i * 0.3) * distortion;
                
                return (
                  <line
                    key={`segment-${j}`}
                    x1={Math.cos(angle1) * (r + modulation)}
                    y1={Math.sin(angle1) * (r + modulation)}
                    x2={Math.cos(angle2) * (r + modulation)}
                    y2={Math.sin(angle2) * (r + modulation)}
                    stroke={i % 3 === 0 ? brand.aqua : i % 3 === 1 ? brand.violet : brand.amber}
                    strokeWidth="2"
                    opacity={0.7 - i * 0.04}
                  />
                );
              })}
            </g>
          );
        })}
        
        {/* Central secure node */}
        <circle cx="0" cy="0" r="20" fill="url(#suzan-glow)" />
        <circle cx="0" cy="0" r="15" fill={brand.aqua} opacity={0.8} />
        <circle cx="0" cy="0" r="10" fill={brand.violet} opacity={0.9} />
        <circle cx="0" cy="0" r="5" fill={brand.white} />
      </g>
      
      {/* Op-art dots - optical trust indicators */}
      {Array.from({ length: 15 }).map((_, ring) =>
        Array.from({ length: ring * 4 + 8 }).map((_, dot) => {
          const angle = (dot / (ring * 4 + 8)) * Math.PI * 2;
          const radius = 100 + ring * 35;
          const cx = 397 + Math.cos(angle) * radius;
          const cy = 280 + Math.sin(angle) * radius;
          const size = 3 + Math.sin(ring * 0.5 + dot * 0.3) * 2;
          const colors = [brand.aqua, brand.violet, brand.amber, brand.white];
          const color = colors[dot % 4];
          
          return (
            <circle
              key={`op-dot-${ring}-${dot}`}
              cx={cx}
              cy={cy}
              r={size}
              fill={color}
              opacity={0.7 - ring * 0.03}
            />
          );
        })
      )}
      
      {/* Secure lattice - geometric trust structure */}
      {Array.from({ length: 8 }).map((_, row) =>
        Array.from({ length: 12 }).map((_, col) => {
          const x = 80 + col * 54;
          const y = 640 + row * 58;
          const distort = Math.sin(col * 0.4 + row * 0.5) * 6;
          
          return (
            <g key={`lattice-${row}-${col}`}>
              <rect
                x={x + distort}
                y={y}
                width={50}
                height={50}
                fill="none"
                stroke={brand.aqua}
                strokeWidth="1.5"
                opacity={0.4}
              />
              {/* Inner security marker */}
              <circle
                cx={x + 25 + distort}
                cy={y + 25}
                r={8 + Math.abs(distort)}
                fill={brand.violet}
                opacity={0.3}
              />
            </g>
          );
        })
      )}
      
      {/* Trust flow lines - data security paths */}
      {Array.from({ length: 20 }).map((_, i) => {
        const y = 80 + i * 12;
        const amplitude = 25 + Math.sin(i * 0.4) * 10;
        
        const path = Array.from({ length: 60 }).map((_, j) => {
          const x = 60 + j * 12;
          const py = y + Math.sin(j * 0.15 + i * 0.25) * amplitude;
          return `${j === 0 ? "M" : "L"} ${x},${py}`;
        }).join(" ");
        
        return (
          <path
            key={`flow-${i}`}
            d={path}
            fill="none"
            stroke={brand.aqua}
            strokeWidth="0.8"
            opacity={0.2}
          />
        );
      })}
      
      {/* AI intelligence nodes - bottom section */}
      {[
        { cx: 150, cy: 1000, connections: [250, 350] },
        { cx: 250, cy: 980, connections: [350, 450] },
        { cx: 350, cy: 1000, connections: [450, 550] },
        { cx: 450, cy: 980, connections: [550, 650] },
        { cx: 550, cy: 1000, connections: [650] },
        { cx: 650, cy: 980, connections: [] },
      ].map((node, i) => (
        <g key={`ai-node-${i}`}>
          {/* Connections to next nodes */}
          {node.connections.map((targetX, j) => {
            const targetY = targetX % 100 === 50 ? 980 : 1000;
            return (
              <line
                key={`connection-${j}`}
                x1={node.cx}
                y1={node.cy}
                x2={targetX}
                y2={targetY}
                stroke={brand.violet}
                strokeWidth="1.5"
                opacity={0.4}
              />
            );
          })}
          
          {/* Node itself */}
          <circle cx={node.cx} cy={node.cy} r="25" fill={brand.aqua} opacity={0.5} />
          <circle cx={node.cx} cy={node.cy} r="18" fill={brand.violet} opacity={0.6} />
          <circle cx={node.cx} cy={node.cy} r="11" fill={brand.amber} opacity={0.7} />
          <circle cx={node.cx} cy={node.cy} r="5" fill={brand.white} opacity={0.9} />
        </g>
      ))}
      
      {/* Signature Vasarely optical squares - Suzan AI branding */}
      {Array.from({ length: 10 }).map((_, i) => {
        const size = 180 - i * 16;
        const offset = i * 8;
        const rotation = i * 3;
        
        return (
          <rect
            key={`signature-${i}`}
            x={120 + offset}
            y={90 + offset}
            width={size}
            height={size}
            fill="none"
            stroke={i % 2 === 0 ? brand.aqua : brand.violet}
            strokeWidth="1"
            opacity={0.15}
            transform={`rotate(${rotation} 210 180)`}
          />
        );
      })}
      
      {/* Trust verification markers */}
      {[
        { x: 100, y: 520, label: "✓" },
        { x: 300, y: 540, label: "✓" },
        { x: 500, y: 520, label: "✓" },
        { x: 700, y: 540, label: "✓" },
      ].map((marker, i) => (
        <g key={`trust-marker-${i}`}>
          <circle cx={marker.x} cy={marker.y} r="18" fill={brand.aqua} opacity={0.3} />
          <circle cx={marker.x} cy={marker.y} r="12" fill={brand.violet} opacity={0.4} />
          <text
            x={marker.x}
            y={marker.y + 6}
            textAnchor="middle"
            fontFamily="sans-serif"
            fontSize="16"
            fill={brand.white}
            fontWeight="bold"
          >
            {marker.label}
          </text>
        </g>
      ))}
      
      {/* Suzan AI wordmark suggestion */}
      <text
        x="397"
        y="1080"
        textAnchor="middle"
        fontFamily="sans-serif"
        fontSize="14"
        fill={brand.aqua}
        opacity={0.5}
        letterSpacing="3"
      >
        SUZAN AI — OPTICAL INTELLIGENCE
      </text>
    </g>
  );
}
